import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import PatientLookupDialog from 'src/components-new/data/PatientLookupDialog'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

export interface RequestSampleValues {
  patient: Record<string, any>
}

interface RequestSampleDialogNewProps {
  open: boolean
  subject: string
  onClose: () => void
  onSave: (values: RequestSampleValues) => void | Promise<void>
}

/**
 * Sample request confirmation dialog, new-style equivalent of
 * src/components/templates/DBSDialog/DBSDIalog.tsx. Modeled on
 * src/pages-new/agenda/VirtualMeetingRequestDialogNew.tsx: collects the form data here
 * and delegates the actual apiClient call / toast / refresh to the parent's onSave.
 */
export default function RequestSampleDialogNew({
  open,
  subject,
  onClose,
  onSave,
}: RequestSampleDialogNewProps): JSX.Element {
  const [patientName, setPatientName] = useState('')
  const [currentPatient, setCurrentPatient] = useState<Record<string, any> | undefined>()
  const [lookupOpen, setLookupOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) {
      setPatientName('')
      setCurrentPatient(undefined)
      setLookupOpen(false)
      setSaving(false)
    }
  }, [open])

  const handleConfirm = async () => {
    if (!currentPatient || saving) return
    setSaving(true)
    try {
      await onSave({patient: currentPatient})
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <AppDialog
        actions={
          <>
            <Button disabled={saving} onClick={onClose} variant="secondary">Annulla</Button>
            <Button disabled={!currentPatient} loading={saving} onClick={handleConfirm} variant="primary">
              Continua
            </Button>
          </>
        }
        maxWidth="sm"
        onClose={onClose}
        open={open}
        title={`Richiedi ${subject}`}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
          <Box>
            <Typography
              sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}
              variant="caption"
            >
              Paziente
            </Typography>
            {currentPatient ? (
              <Box sx={{mt: 0.5}}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    backgroundColor: NEUTRAL_SCALE[50],
                    border: `1px solid ${NEUTRAL_SCALE[200]}`,
                  }}
                >
                  <Typography variant="body2">{patientName}</Typography>
                  <ActionIconButton
                    onClick={() => {
                      setCurrentPatient(undefined)
                      setPatientName('')
                    }}
                    tooltip="Rimuovi"
                    variant="close"
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{display: 'flex', gap: 1, mt: 0.5}}>
                <Button onClick={() => setLookupOpen(true)} startIcon={<SearchIcon fontSize="small" />} variant="ghost">
                  Aggiungi Paziente
                </Button>
              </Box>
            )}
          </Box>
          <Typography variant="body2" sx={{color: 'text.secondary'}}>
            {`Confermi la richiesta di un ${subject} per il paziente selezionato?`}
          </Typography>
        </Box>
      </AppDialog>

      {lookupOpen && (
        <PatientLookupDialog
          onClose={() => setLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentPatient(row)
            setPatientName(`${row.firstName ?? ''} ${row.lastName ?? ''}`.trim())
            setLookupOpen(false)
          }}
        />
      )}
    </>
  )
}